import { clsx, type ClassValue } from 'clsx'
import { twMerge } from 'tailwind-merge'

export function cn(...inputs: ClassValue[]) {
  return twMerge(clsx(inputs))
}

import { AxiosError, isAxiosError } from 'axios'
import { StudentDecision } from '@/redux/slices/studentDecisionWebsocketSlice'

type ErrorResponse = {
  error: string
  validation_errors?: Array<{ loc: string[]; msg: string; type: string }>
}

export const getErrorMessage = (error: unknown): string => {
  if (isAxiosError(error)) {
    const data = (error as AxiosError<ErrorResponse>).response?.data
    if (data?.validation_errors?.length) {
      const lines = data.validation_errors
        .slice(0, 5)
        .map((e) => `${e.loc.join('.')}: ${e.msg}`)
      return [data.error || 'Validation failed', ...lines].join('\n')
    }
    return data?.error || 'An unexpected error occurred'
  }
  return 'An unexpected error occurred'
}

export type StructuredApiError = {
  code: string
  message: string
}

export const getStructuredError = (error: unknown): StructuredApiError => {
  if (isAxiosError(error)) {
    const data = (error as AxiosError<StructuredApiError>).response?.data
    if (data?.code && data?.message) {
      return { code: data.code, message: data.message }
    }
  }
  return {
    code: 'UNKNOWN_ERROR',
    message: 'Something went wrong. Please try again.',
  }
}

/**
 * Escapes special characters in a string for use in a regular expression.
 * @param string - The string to escape.
 * @returns The escaped string safe for use in regex patterns.
 */
export const escapeRegExp = (string: string): string => {
  return string.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')
}

export const isHtml = (text: string) =>
  /<\/?[a-z][\s\S]*>/i.test(text) ||
  text.includes('&lt;') ||
  text.includes('&gt;')

export const findUserDecision = (
  decisions: StudentDecision[] | null | undefined,
  userId: number | undefined,
  phaseId: number | undefined | null
) => {
  if (!userId || !phaseId) return undefined

  return decisions?.find(
    (decision: StudentDecision) =>
      decision.student_id === userId && decision.phase_id === phaseId
  )
}

export const triggerBlobDownload = (blob: Blob, filename: string): void => {
  const url = URL.createObjectURL(blob)
  const a = document.createElement('a')
  a.href = url
  a.download = filename
  document.body.appendChild(a)
  a.click()
  document.body.removeChild(a)
  URL.revokeObjectURL(url)
}

export const findTeamDecision = (
  decisions: StudentDecision[] | null | undefined,
  communicatorStudentId: number | undefined,
  phaseId: number | undefined | null
) => {
  if (!communicatorStudentId || !phaseId) return undefined

  return decisions?.find(
    (decision: StudentDecision) =>
      decision.student_id === communicatorStudentId &&
      decision.phase_id === phaseId
  )
}
